import { useCallback, useState, type ReactNode } from "react";
import { Button, Tooltip } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LaserReviewIcon } from "@/components/ui/mui/LaserReviewIcon";
import { useLaseredConditions } from "@/resources/policies/hooks/useLaseredConditions";
import { useDeductibleReviews } from "@/resources/claimants/hooks/useDeductibleReviews";
import { useRecomputeRefreshOrStandalone } from "@/resources/claimants/hooks/useRecomputeRefresh";
import { DeductibleReviewDialog } from "@/resources/claimants/components/deductible-review/DeductibleReviewDialog";
import {
  reviewFlagAriaLabel,
  reviewHelperText,
} from "@/resources/claimants/components/deductible-review/reviewHelperText";

interface DeductibleReviewFlagProps {
  claimantId: string;
  policyId: string;
  claimantName?: string;
  /** `icon` matches the compact laser chips; `chip` matches labelled chips. */
  variant?: "icon" | "chip";
  /** The policy uses ASD allocation (not held); pass where the policy is loaded. */
  asdPolicy?: boolean;
}

/**
 * Review variant of the laser chip (#7345): warning colour plus an attention
 * icon, a reason-specific tooltip, and click / Enter / Space to open the
 * resolution dialog. Keyboard-focusable with an accessible name that names the
 * claimant and the hold. Used by the claimant, policy and client pages, so it
 * lives with the other laser chips.
 */
export const DeductibleReviewFlag = ({
  claimantId,
  policyId,
  claimantName,
  variant = "icon",
  asdPolicy,
}: DeductibleReviewFlagProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { openReview } = useDeductibleReviews(claimantId, policyId);
  const { conditions } = useLaseredConditions(policyId, claimantId);
  const text = reviewHelperText(openReview, conditions);
  // The flag outlives the dialog, so it owns the post-resolve refresh (the
  // dialog's own timers would die with it). Off the claimant page that is a
  // standalone instance; the flag itself unmounts once the refreshed row is no
  // longer pending, which is the point of the refresh.
  const { triggerRecomputeRefresh } = useRecomputeRefreshOrStandalone();
  const handleResolved = useCallback(() => triggerRecomputeRefresh({ notifyUser: false }), [triggerRecomputeRefresh]);

  const open = (e: { stopPropagation: () => void; preventDefault?: () => void }) => {
    // Flags sit inside clickable rows; do not navigate the row.
    e.stopPropagation();
    e.preventDefault?.();
    setDialogOpen(true);
  };

  const ariaLabel = reviewFlagAriaLabel(claimantName);

  // Real buttons with the laser-plus-"!" mark (Blake, local test 2026-10-06:
  // a bare label read as a status; the warning triangle lost the laser cue).
  // "chip" is the secondary action on detail surfaces (the claimant banner is
  // the primary one); "icon" is the compact form for table cells. Neither
  // shrinks: in a flex row beside a long name it would spill over the next
  // cell (UX review #1).
  const trigger =
    variant === "chip" ? (
      <Button
        size="small"
        variant="outlined"
        color="warning"
        startIcon={<LaserReviewIcon />}
        endIcon={<ChevronRightIcon />}
        onClick={open}
        aria-label={ariaLabel}
        data-testid="deductible-review-flag"
        sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0 }}
      >
        Review deductible
      </Button>
    ) : (
      <Button
        size="small"
        variant="outlined"
        color="warning"
        startIcon={<LaserReviewIcon size={14} />}
        onClick={open}
        aria-label={ariaLabel}
        data-testid="deductible-review-flag"
        sx={{
          textTransform: "none",
          fontWeight: 600,
          minWidth: 0,
          // 24 px target (WCAG 2.5.8) inside clickable rows.
          minHeight: 24,
          flexShrink: 0,
          py: 0,
          px: 0.75,
          lineHeight: 1.6,
          fontSize: "0.75rem",
          whiteSpace: "nowrap",
          "& .MuiButton-startIcon": { mr: 0.5 },
        }}
      >
        Review
      </Button>
    );

  return (
    <>
      <Tooltip title={text} arrow describeChild>
        {trigger}
      </Tooltip>
      {dialogOpen && (
        <DeductibleReviewDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onResolved={handleResolved}
          claimantId={claimantId}
          policyId={policyId}
          claimantName={claimantName}
          asdPolicy={asdPolicy}
        />
      )}
    </>
  );
};

interface LaserChipWithReviewProps extends Omit<DeductibleReviewFlagProps, "claimantId" | "policyId"> {
  pending?: boolean;
  claimantId?: string;
  policyId?: string;
  /** The normal laser chip; may be null when the claimant has no laser chip. */
  children?: ReactNode;
}

/**
 * Wrap any laser chip render site: while a review is pending the review flag
 * replaces the chip (and appears even where no laser chip would show, e.g. a
 * tie between two conditions on an otherwise base deductible).
 */
export const LaserChipWithReview = ({ pending, claimantId, policyId, children, ...flag }: LaserChipWithReviewProps) => {
  if (!pending || !claimantId || !policyId) return <>{children}</>;
  return <DeductibleReviewFlag claimantId={claimantId} policyId={policyId} {...flag} />;
};
